/**
 * Deterministic place names.
 *
 * Syllable assembly rather than a fixed list, so the namespace does not run out
 * as the universe grows and no name table has to be stored.
 *
 * ── Why the grammar is this wide ────────────────────────────────────────────
 *
 * An earlier version was onset + vowel + coda: 3,640 possible names. That was
 * fine for a few hundred systems and quietly broke at twelve thousand, where
 * the pigeonhole principle alone forced heavy repetition — one name landed on
 * thirteen different systems.
 *
 * Adding an optional interior syllable takes the space past a million, so
 * collisions become rare rather than guaranteed. If the universe grows another
 * order of magnitude, check `scripts/analyse-world.mjs` again before assuming
 * this still holds.
 */
const ONSETS = [
    'Ax', 'Bel', 'Cor', 'Dra', 'Eri', 'Fen', 'Gal', 'Hel', 'Ith', 'Jor',
    'Kel', 'Lyr', 'Mor', 'Nyx', 'Oph', 'Pra', 'Quin', 'Rhe', 'Sol', 'Tor',
    'Umb', 'Vex', 'Wren', 'Xan', 'Yr', 'Zel', 'Ald', 'Brae', 'Cyn', 'Dor',
    'Esk', 'Fyr', 'Gro', 'Hax', 'Isk', 'Kry', 'Lom', 'Mir', 'Nor', 'Ves', 'Mi', 'Fo'
];
const VOWELS = ['a', 'e', 'i', 'o', 'u', 'ae', 'ar', 'en', 'il', 'or', 'ys', 'ia'];
/**
 * Plain vowels, used either side of a bridge.
 *
 * The compound vowels above ("ys", "il") are fine on their own but collide
 * badly with a following consonant — "Esk" + "ys" + "ct" + "ys" + "orn" is a
 * real name the first attempt produced. Interior syllables stay simple.
 */
const PURE_VOWELS = ['a', 'e', 'i', 'o', 'u'];
/** Interior consonants, kept soft so the extra syllable stays pronounceable. */
const BRIDGES = [
    'l', 'r', 'n', 'th', 'd', 'm', 'v', 's', 't', 'k', 'ph', 'nd',
];
const CODAS = [
    'on', 'is', 'ar', 'ex', 'ium', 'os', 'eth', 'ax', 'une', 'ir',
    'ade', 'ock', 'ynt', 'us', 'iel', 'orn', 'eus', 'ara', 'yth', 'ix', 'mo'
];
/** Roughly this often, a name gains an interior syllable and reads longer. */
const BRIDGE_CHANCE = 0.45;
/**
 * Two shapes, chosen per name:
 *   short  — onset + vowel + coda                    (40 x 12 x 20  =   9,600)
 *   long   — onset + vowel + bridge + vowel + coda    (40 x 5 x 12 x 5 x 20 = 240,000)
 *
 * A quarter of a million possibilities against twelve thousand systems, so
 * repeats are occasional rather than inevitable.
 */
export function makeName(rng) {
    const onset = rng.pick(ONSETS);
    const coda = rng.pick(CODAS);
    // Every draw happens either way, so the stream advances the same number of
    // steps regardless of which shape is chosen — otherwise whether one name was
    // long would shift every name generated after it.
    const simpleVowel = rng.pick(VOWELS);
    const openVowel = rng.pick(PURE_VOWELS);
    const bridge = rng.pick(BRIDGES);
    const closeVowel = rng.pick(PURE_VOWELS);
    const long = rng.bool(BRIDGE_CHANCE);
    return long
        ? `${onset}${openVowel}${bridge}${closeVowel}${coda}`
        : `${onset}${simpleVowel}${coda}`;
}
/**
 * A name with a catalogue suffix, e.g. "Vexaris IV".
 *
 * Used where many similar things sit side by side — planets in one system — so
 * they read as related rather than unconnected.
 */
export function makeDesignatedName(base, index) {
    const numerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
    return `${base} ${numerals[index] ?? String(index + 1)}`;
}
const STATION_TITLES = [
    'Anchorage', 'Bastion', 'Crossing', 'Depot', 'Foundry', 'Gate', 'Platform',
    'Haven', 'Junction', 'Keep', 'Landing', 'Post', 'Reach', 'Watch', 'Yard', 'Station',
];
/** e.g. "Kelor Anchorage". */
export function makeStationName(rng) {
    return `${rng.pick(ONSETS)}${rng.pick(VOWELS)} ${rng.pick(STATION_TITLES)}`;
}
// ─── People ──────────────────────────────────────────────────────────────────
/**
 * Given names.
 *
 * Built from the same onsets as everything else so a person sounds like they
 * come from this galaxy rather than from a fantasy novel that wandered in. The
 * endings are what make them read as people: places take `-ock`, `-aax`,
 * `-undeade`, and nobody is called that.
 */
const PERSON_ENDINGS = [
    'a', 'ac', 'ah', 'an', 'as', 'ek', 'el', 'en', 'es', 'et',
    'ia', 'ik', 'in', 'is', 'it', 'ok', 'on', 'or', 'os', 'us',
];
/**
 * Surnames.
 *
 * Deliberately plainer than the given names and drawn from a fixed list, so a
 * full name has one invented half and one familiar one. Two invented halves
 * together stop parsing as a name at all.
 */
const SURNAMES = [
    'Ardent', 'Blackwell', 'Carrow', 'Dunmore', 'Ellery', 'Faraday', 'Gorse',
    'Halloway', 'Ingram', 'Jarrow', 'Kestrel', 'Lowen', 'Marchetti', 'Nevin',
    'Oakes', 'Pell', 'Quill', 'Raines', 'Sable', 'Thorne', 'Underwood',
    'Vance', 'Wray', 'Yarrow', 'Ziegler', 'Ashgrove', 'Bracken', 'Corvid',
    'Delacroix', 'Emberly', 'Frost', 'Grieves', 'Hollis', 'Isley', 'Kaur',
    'Lindqvist', 'Moreau', 'Nakamura', 'Okonkwo', 'Petrov', 'Reyes', 'Silva',
];
/** A given name on its own, e.g. "Kelis". */
export function makeGivenName(rng) {
    return `${rng.pick(ONSETS)}${rng.pick(PERSON_ENDINGS)}`;
}
/** A full name, e.g. "Kelis Marchetti". */
export function makePersonName(rng) {
    return `${makeGivenName(rng)} ${rng.pick(SURNAMES)}`;
}
/**
 * Ranks and posts, for people who are speaking in an official capacity.
 *
 * Titles rather than jobs: a player wants to know whether the voice on the
 * radio outranks them, not what its day looks like.
 */
const RANKS = [
    'Controller', 'Officer', 'Supervisor', 'Warden', 'Marshal',
    'Coordinator', 'Inspector', 'Handler', 'Registrar', 'Deputy',
];
export function makeOfficial(rng, post) {
    const rank = rng.pick(RANKS);
    const name = makePersonName(rng);
    return { rank, name, signature: `${rank} ${name}, ${post}` };
}
//# sourceMappingURL=names.js.map